import React, { useState, useEffect } from 'react';
import { Arzt, Abwesenheit, CreateAbwesenheitDTO, AppType } from '../../types';
import { abwesenheitService } from '../../services/abwesenheit.service';
import { arztService } from '../../services/arzt.service';
import { formatDate } from '../../utils/dateUtils';
import { useEnvironment } from '../../context/EnvironmentContext';

interface AbwesenheitDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
}

const AbwesenheitDialog: React.FC<AbwesenheitDialogProps> = ({
    isOpen,
    onClose,
    onUpdate,
}) => {
    const { currentApp } = useEnvironment();
    const [aerzte, setAerzte] = useState<Arzt[]>([]);
    const [abwesenheiten, setAbwesenheiten] = useState<Abwesenheit[]>([]);
    const [selectedArztId, setSelectedArztId] = useState<number | ''>('');
    const [startDatum, setStartDatum] = useState('');
    const [endDatum, setEndDatum] = useState('');
    const [typ, setTyp] = useState('Urlaub');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = React.useCallback(async () => {
        try {
            setLoading(true);
            const [aerzteData, abwesenheitenData] = await Promise.all([
                arztService.getAllAerzte(currentApp as AppType),
                abwesenheitService.getAllAbwesenheiten({ app: currentApp as AppType }),
            ]);
            setAerzte(aerzteData);
            setAbwesenheiten(abwesenheitenData);
        } catch (err) {
            setError('Fehler beim Laden der Daten');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [currentApp]);

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen, fetchData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedArztId || !startDatum || !endDatum) return;

        await createAbwesenheit(false);
    };

    const createAbwesenheit = async (force: boolean) => {
        try {
            setLoading(true);
            setError(null);
            const newAbwesenheit: CreateAbwesenheitDTO = {
                arztId: Number(selectedArztId),
                startDatum: new Date(startDatum),
                endDatum: new Date(endDatum),
                typ,
                force,
            };

            await abwesenheitService.createAbwesenheit(newAbwesenheit);
            await fetchData();
            onUpdate();

            // Reset form
            setSelectedArztId('');
            setStartDatum('');
            setEndDatum('');
            setTyp('Urlaub');
        } catch (err: any) {
            if (err.response?.status === 409 && !force) {
                const conflictCount = err.response.data.conflictCount;
                if (window.confirm(`Der Arzt ist in diesem Zeitraum bereits für ${conflictCount} Schichten eingeteilt. Sollen diese Zuweisungen entfernt werden?`)) {
                    await createAbwesenheit(true);
                }
            } else {
                setError('Fehler beim Erstellen der Abwesenheit');
                console.error(err);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Möchten Sie diese Abwesenheit wirklich löschen?')) return;

        try {
            setLoading(true);
            await abwesenheitService.deleteAbwesenheit(id);
            await fetchData();
            onUpdate();
        } catch (err) {
            setError('Fehler beim Löschen der Abwesenheit');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Abwesenheiten verwalten</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-700 p-3 rounded mb-4">{error}</div>
                )}

                <form onSubmit={handleSubmit} className="mb-8 bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Neue Abwesenheit eintragen</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Arzt
                            </label>
                            <select
                                value={selectedArztId}
                                onChange={(e) => setSelectedArztId(Number(e.target.value))}
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                required
                            >
                                <option value="">Bitte wählen...</option>
                                {aerzte.map((arzt) => (
                                    <option key={arzt.id} value={arzt.id}>
                                        {arzt.vorname} {arzt.nachname}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Typ
                            </label>
                            <select
                                value={typ}
                                onChange={(e) => setTyp(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2"
                            >
                                <option value="Urlaub">Urlaub</option>
                                <option value="Krankheit">Krankheit</option>
                                <option value="Fortbildung">Fortbildung</option>
                                <option value="Sonstiges">Sonstiges</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Von
                            </label>
                            <input
                                type="date"
                                value={startDatum}
                                onChange={(e) => setStartDatum(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Bis
                            </label>
                            <input
                                type="date"
                                value={endDatum}
                                onChange={(e) => setEndDatum(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                required
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? 'Speichert...' : 'Hinzufügen'}
                        </button>
                    </div>
                </form>

                <div>
                    <h3 className="font-semibold mb-3">Aktuelle Abwesenheiten</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Arzt</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Zeitraum</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {abwesenheiten.map((abwesenheit) => (
                                    <tr key={abwesenheit.id}>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                            {abwesenheit.arzt?.vorname} {abwesenheit.arzt?.nachname}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(new Date(abwesenheit.startDatum))} - {formatDate(new Date(abwesenheit.endDatum))}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                            {abwesenheit.typ}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                            <button
                                                onClick={() => handleDelete(abwesenheit.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Löschen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AbwesenheitDialog;
